import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FormDialog, Field } from "./form-dialog";
import { nameField, emailField, optionalText, monthField } from "@/lib/validators";

const schema = z.object({
  name: nameField(120),
  company: optionalText(120, /^[\p{L}\p{N}\s'’\-.,&()/]+$/u),
  email: emailField,
  type: z.enum(["editorial", "commercial", "portrait", "wedding", "product"]),
  since: monthField,
});
type Values = z.infer<typeof schema>;

export function NewClientDialog() {
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, watch, reset } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", company: "", email: "", type: "commercial", since: "" },
  });
  return (
    <FormDialog
      trigger={<Button size="sm" className="gap-1.5"><Plus className="size-3.5" /> Add client</Button>}
      title="Add a client"
      description="Create a client record. You can attach inquiries and shoots after."
      submitLabel="Add client"
      isDirty={isDirty}
      onReset={() => reset()}
      onSubmit={() => new Promise<void>((resolve, reject) => {
        handleSubmit((v) => {
          toast.success("Client added", { description: v.name });
          resolve();
        }, () => reject())();
      })}
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name" htmlFor="cl-name" error={errors.name?.message}>
          <Input id="cl-name" placeholder="Elena Rossi" {...register("name")} />
        </Field>
        <Field label="Company" htmlFor="cl-company" error={errors.company?.message}>
          <Input id="cl-company" placeholder="Optional" {...register("company")} />
        </Field>
      </div>
      <Field label="Email" htmlFor="cl-email" error={errors.email?.message}>
        <Input id="cl-email" type="email" placeholder="name@studio.com" {...register("email")} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Type" error={errors.type?.message}>
          <Select value={watch("type")} onValueChange={(v) => setValue("type", v as Values["type"])}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="editorial">Editorial</SelectItem>
              <SelectItem value="commercial">Commercial</SelectItem>
              <SelectItem value="portrait">Portrait</SelectItem>
              <SelectItem value="wedding">Wedding</SelectItem>
              <SelectItem value="product">Product</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Client since" htmlFor="cl-since" error={errors.since?.message}>
          <Input id="cl-since" type="month" {...register("since")} />
        </Field>
      </div>
    </FormDialog>
  );
}
